import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type ComponentType, Suspense, lazy, useState } from "react";

// CR-246 S2.6: the Feedback dialog is loaded on the first click so it stays
// out of the initial bundle (the header is in it, see .size-limit.js).
// A chunk that fails to load (a deploy replaced its hash) must not take the
// page down through the root error boundary: the fallback asks for a reload.
type FeedbackDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** CR-302: the viewed project, the api-server's context for the contributor check. */
  projectId?: string;
};

export function loadFeedbackDialog(
  importDialog: () => Promise<{
    AppFeedbackDialog: ComponentType<FeedbackDialogProps>;
  }> = () => import("@/features/product-feedback/components/app-feedback-dialog"),
): Promise<{ default: ComponentType<FeedbackDialogProps> }> {
  return importDialog()
    .then((module) => ({ default: module.AppFeedbackDialog }))
    .catch(() => ({ default: FeedbackLoadFailedDialog }));
}

const AppFeedbackDialog = lazy(() => loadFeedbackDialog());

export function FeedbackLoadFailedDialog({ open, onOpenChange }: FeedbackDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="app-feedback-load-failed">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            The feedback form could not be loaded. Please reload the page and try again.
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

/**
 * CR-246 D8: send feedback about Kairo from any page, in place. Shown to
 * every signed-in person, incognito included, except a contributor in the
 * current context (CR-302); hidden in the logo-only invite header.
 * `projectId` is the viewed project, forwarded to the dialog as context.
 */
export const FeedbackAction = ({ projectId }: { projectId?: string }) => {
  const [open, setOpen] = useState(false);
  // Stays true once clicked, so reopening never re-suspends the loaded chunk.
  const [everOpened, setEverOpened] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="header-feedback"
        className="hover:text-primary text-sm font-medium"
        onClick={() => {
          setEverOpened(true);
          setOpen(true);
        }}
      >
        Feedback
      </button>
      {everOpened ? (
        <Suspense fallback={null}>
          <AppFeedbackDialog open={open} onOpenChange={setOpen} projectId={projectId} />
        </Suspense>
      ) : null}
    </>
  );
};
